// حذف بيانات فترة — الشاشة الوحيدة التي تمحو بيانات بلا رجعة.
//
// قاعدتان تحكمانها:
// ١) لا تسأل «هل أنت متأكد؟» مجرّداً. اعرض ما سيختفي أوّلاً — كم حركة وبكم —
//    فالقرار يُتّخذ على معلومة لا على ثقة بالذاكرة.
// ٢) التأكيد كتابةً لا نقراً. النقر المزدوج يصير حركة آلية بعد مرّتين؛ الكتابة
//    تُجبر على التوقّف. والكلمة تُقبل بالعربية أيضاً: لوحة المستخدم عربية،
//    وفرض الإنجليزية إزعاج لا حاجز أمان.

import { fmt, esc, ymd, fromYmd, monthStart, startOfDay, shortDate, MONTHS, DAY_MS } from '../util.js';
import { baseSym } from '../store.js';
import { api, ApiError } from '../api.js';
import { backBar } from '../ui.js';

const CONFIRM_WORDS = ['DELETE', 'احذف'];

export function freshDraft(){
  const prev = monthStart(Date.now(), 1);
  return {
    mode: 'prev',                       // prev | year | range
    year: new Date().getFullYear() - 1,
    from: prev,
    to: monthStart(Date.now(), 0),
    preview: null,                      // { count, total, firstAt, lastAt }
    loading: false,
    stage: 'idle',                      // idle | confirm | done
    word: '',
    err: null,
    deleted: 0,
  };
}

/** حدود الفترة المختارة — المدى [from, to) كما يفهمه الخادم. */
export function bounds(d){
  if (d.mode === 'prev'){
    return { from: monthStart(Date.now(), 1), to: monthStart(Date.now(), 0) };
  }
  if (d.mode === 'year'){
    return {
      from: new Date(d.year, 0, 1).getTime(),
      to: new Date(d.year + 1, 0, 1).getTime(),
    };
  }
  // النهاية يوم كامل: من اختار «إلى ١٥ آب» يقصد نهاية ذلك اليوم لا بدايته
  return { from: startOfDay(d.from), to: startOfDay(d.to) + DAY_MS };
}

// أسماء الشهور من MONTHS لا من toLocaleDateString: الأخيرة تعطي «سبتمبر»
// بينما التطبيق كلّه يقول «أيلول»، فيبدو الاسمان كأنهما شهران مختلفان.
function monthLabel(ts){
  const d = new Date(ts);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function years(){
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2, now - 3, now - 4];
}

function periodControls(d){
  if (d.mode === 'prev'){
    return `<p class="desc">${esc(monthLabel(bounds(d).from))} بأكمله</p>`;
  }
  if (d.mode === 'year'){
    return `<div class="field">
      <label for="pgYear">السنة</label>
      <select id="pgYear">
        ${years().map(y => `<option value="${y}" ${y === d.year ? 'selected' : ''}>${y}</option>`).join('')}
      </select>
    </div>`;
  }
  return `<div class="dates" style="padding:0 0 10px">
    <label>من<input type="date" id="pgFrom" value="${ymd(d.from)}"></label>
    <label>إلى<input type="date" id="pgTo" value="${ymd(d.to)}"></label>
  </div>`;
}

function previewBlock(d){
  if (d.loading) return '<p class="desc">جارٍ الحساب…</p>';
  if (d.err) return `<p class="desc" style="color:var(--up)">${esc(d.err)}</p>`;
  if (!d.preview) return '';

  const { count, total, firstAt, lastAt } = d.preview;
  if (!count) return '<p class="desc">لا حركات في هذه الفترة.</p>';

  return `<div class="pgsum">
    <span class="lbl">سيُحذف</span>
    <b>${fmt(count)} حركة · ${fmt(Number(total))} ${esc(baseSym())}</b>
    ${firstAt && lastAt
      ? `<span class="lbl">من ${esc(shortDate(new Date(firstAt).getTime()))} إلى ${esc(shortDate(new Date(lastAt).getTime()))}</span>`
      : ''}
  </div>`;
}

function actions(d){
  if (d.stage === 'done'){
    return `<p class="desc" style="color:var(--in)">حُذفت ${fmt(d.deleted)} حركة.</p>`;
  }

  if (d.stage === 'confirm'){
    return `<div class="pgconfirm">
      <p>${fmt(d.preview.count)} حركة ستُحذف نهائياً، ولا يمكن التراجع.
         اكتب <b>احذف</b> أو <b>DELETE</b> للمتابعة.</p>
      <div class="field" style="margin-bottom:10px">
        <input id="pgWord" type="text" autocomplete="off" placeholder="احذف" value="${esc(d.word)}">
      </div>
      <div class="btnrow">
        <button type="button" class="btn" data-act="pg-cancel">تراجع</button>
        <button type="button" class="btn danger" data-act="pg-confirm">حذف نهائي</button>
      </div>
    </div>`;
  }

  const n = d.preview && d.preview.count;
  if (!n) return '';
  return `<div class="btnrow">
    <button type="button" class="btn danger" data-act="pg-start">حذف حركات هذه الفترة</button>
  </div>`;
}

export function render(app){
  const d = app.purgeDraft;
  const modes = [['prev', 'الشهر السابق'], ['year', 'سنة كاملة'], ['range', 'بين تاريخين']];

  return backBar('حذف بيانات فترة', 'لا يمكن التراجع بعد الحذف')
    + `<div class="scroll">
      <div class="card">
        <h3>الفترة</h3>
        <div class="seg" style="padding:0 0 10px">
          ${modes.map(([m, label]) =>
            `<button type="button" data-pgmode="${m}" aria-pressed="${d.mode === m}">${label}</button>`
          ).join('')}
        </div>
        ${periodControls(d)}
        ${previewBlock(d)}
        ${actions(d)}
      </div>
    </div>`;
}

/** يسأل الخادم ماذا سيُحذف — بعد كل تغيير في الفترة. */
export async function refreshPreview(app){
  const d = app.purgeDraft;
  const { from, to } = bounds(d);

  d.stage = 'idle';
  d.word = '';
  d.err = null;

  if (from >= to){
    d.preview = null;
    d.err = 'بداية الفترة يجب أن تسبق نهايتها';
    return app.render();
  }

  d.loading = true;
  app.render();
  try {
    d.preview = await api.purgePreview(from, to);
  } catch (err){
    d.preview = null;
    d.err = err instanceof ApiError && err.isOffline
      ? 'يحتاج اتصالاً'
      : (err.message || 'تعذّر حساب الفترة');
  } finally {
    d.loading = false;
    app.render();
  }
}

export function mount(app, root){
  const d = app.purgeDraft;

  const year = root.querySelector('#pgYear');
  if (year) year.onchange = () => { d.year = Number(year.value); refreshPreview(app); };

  const from = root.querySelector('#pgFrom');
  const to = root.querySelector('#pgTo');
  if (from) from.onchange = () => { d.from = fromYmd(from.value); refreshPreview(app); };
  if (to) to.onchange = () => { d.to = fromYmd(to.value); refreshPreview(app); };

  const word = root.querySelector('#pgWord');
  if (word){
    word.focus();
    word.oninput = () => { d.word = word.value; };
  }

  if (!d.preview && !d.loading && !d.err) refreshPreview(app);
}

export function onClick(app, btn){
  const d = app.purgeDraft;

  if (btn.dataset.pgmode){
    d.mode = btn.dataset.pgmode;
    refreshPreview(app);
    return true;
  }

  const act = btn.dataset.act;

  if (act === 'pg-start'){
    d.stage = 'confirm';
    d.word = '';
    app.render();
    return true;
  }

  if (act === 'pg-cancel'){
    d.stage = 'idle';
    d.word = '';
    app.render();
    return true;
  }

  if (act === 'pg-confirm'){
    if (!CONFIRM_WORDS.includes(d.word.trim().toUpperCase())
        && !CONFIRM_WORDS.includes(d.word.trim())){
      app.toast('اكتب «احذف» أو DELETE للمتابعة');
      return true;
    }
    return runPurge(app);
  }

  return false;
}

async function runPurge(app){
  const d = app.purgeDraft;
  const { from, to } = bounds(d);

  try {
    const res = await api.purgeRange(from, to);
    d.deleted = res.deleted;
    d.stage = 'done';
    d.word = '';
    // العدّادات ومجموع الشهر تُبنى من الخادم — بلا تحديث تبقى الشاشة الرئيسية
    // تعرض أرقاماً لحركات لم تعد موجودة
    await app.hardRefresh();
    await refreshPreview(app);
    d.stage = 'done';
    app.render();
    app.toast(`حُذفت ${fmt(res.deleted)} حركة`);
  } catch (err){
    d.stage = 'idle';
    app.toast(err instanceof ApiError && err.isOffline
      ? 'الحذف يحتاج اتصالاً' : (err.message || 'تعذّر الحذف'));
    app.render();
  }
  return true;
}
